import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, Alert } from 'react-native';
import tw from 'twrnc';

import HeaderBar from '../../../components/HeaderBar';
import ScreenWrapper from '../../../components/ScreenWrapper';
import { t } from '../../../i18n/translations';
import PremiumBackgroundUniversal from '../../../shared/ui/PremiumBackgroundUniversal';
import { translationQualityService, TranslationContribution } from '../services/translationQualityService';
import { useLanguageStore } from '../stores/languageStore';

interface CommunityTranslationScreenProps {
  navigation: any;
}

export const CommunityTranslationScreen: React.FC<CommunityTranslationScreenProps> = ({ navigation }) => {
  const { currentLanguage } = useLanguageStore();
  const [activeTab, setActiveTab] = useState<'contribute' | 'review' | 'leaderboard'>('contribute');
  const [contributions, setContributions] = useState<TranslationContribution[]>([]);
  const [translationsToReview, setTranslationsToReview] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState<TranslationContribution | null>(null);

  useEffect(() => {
    loadData();
  }, [activeTab, currentLanguage]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'contribute': {
          const pendingContributions = await translationQualityService.getPendingContributions(currentLanguage);
          setContributions(pendingContributions);
          break;
        }
        case 'review': {
          const reviewTranslations = await translationQualityService.getTranslationsNeedingReview(currentLanguage);
          setTranslationsToReview(reviewTranslations);
          break;
        }
        case 'leaderboard': {
          const contributors = await translationQualityService.getContributorLeaderboard(currentLanguage);
          setLeaderboard(contributors);
          break;
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (contributionId: string, voteType: 'up' | 'down') => {
    try {
      // Get current user ID (would come from auth context)
      const userId = 'current-user-id'; // Replace with actual user ID
      await translationQualityService.voteOnContribution(contributionId, userId, voteType);
      
      // Refresh contributions
      loadData();
      
      Alert.alert(
        t('community_translation.vote_success_title', currentLanguage),
        t('community_translation.vote_success_message', currentLanguage)
      );
    } catch (error) {
      Alert.alert(
        t('community_translation.vote_error_title', currentLanguage),
        t('community_translation.vote_error_message', currentLanguage)
      );
    }
  };

  const renderContributeTab = () => (
    <View style={tw`flex-1`}>
      <View style={tw`p-4 bg-blue-50 rounded-lg mb-4`}>
        <Text style={tw`text-lg font-bold text-blue-800 mb-2`}>
          {t('community_translation.contribute_title', currentLanguage)}
        </Text>
        <Text style={tw`text-blue-600`}>
          {t('community_translation.contribute_description', currentLanguage)}
        </Text>
      </View>

      <ScrollView style={tw`flex-1`}>
        {contributions.map((contribution) => (
          <ContributionCard
            key={contribution.id}
            contribution={contribution}
            onVote={handleVote}
            onSelect={() => setSelectedContribution(contribution)}
            language={currentLanguage}
          />
        ))}
        
        {contributions.length === 0 && !loading && (
          <View style={tw`p-8 items-center`}>
            <Text style={tw`text-gray-500 text-center`}>
              {t('community_translation.no_contributions', currentLanguage)}
            </Text>
          </View>
        )}
      </ScrollView>

      <TouchableOpacity
        style={tw`bg-blue-600 p-4 rounded-lg m-4`}
        onPress={() => navigation.navigate('SubmitTranslation')}
      >
        <Text style={tw`text-white text-center font-bold`}>
          {t('community_translation.submit_translation', currentLanguage)}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderReviewTab = () => (
    <View style={tw`flex-1`}>
      <View style={tw`p-4 bg-green-50 rounded-lg mb-4`}>
        <Text style={tw`text-lg font-bold text-green-800 mb-2`}>
          {t('community_translation.review_title', currentLanguage)}
        </Text>
        <Text style={tw`text-green-600`}>
          {t('community_translation.review_description', currentLanguage)}
        </Text>
      </View>

      <ScrollView style={tw`flex-1`}>
        {translationsToReview.map((translation) => (
          <ReviewCard
            key={translation.id}
            translation={translation}
            language={currentLanguage}
            onReview={(translationId, approved) => {
              // Handle review submission
              console.log('Review:', translationId, approved);
            }}
          />
        ))}
        
        {translationsToReview.length === 0 && !loading && (
          <View style={tw`p-8 items-center`}>
            <Text style={tw`text-gray-500 text-center`}>
              {t('community_translation.no_reviews_needed', currentLanguage)}
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );

  const renderLeaderboardTab = () => (
    <View style={tw`flex-1`}>
      <View style={tw`p-4 bg-yellow-50 rounded-lg mb-4`}>
        <Text style={tw`text-lg font-bold text-yellow-800 mb-2`}>
          {t('community_translation.leaderboard_title', currentLanguage)}
        </Text>
        <Text style={tw`text-yellow-600`}>
          {t('community_translation.leaderboard_description', currentLanguage)}
        </Text>
      </View>

      <ScrollView style={tw`flex-1`}>
        {leaderboard.map((contributor, index) => (
          <LeaderboardCard
            key={contributor.contributor_id}
            contributor={contributor}
            rank={index + 1}
            language={currentLanguage}
          />
        ))}
        
        {leaderboard.length === 0 && !loading && (
          <View style={tw`p-8 items-center`}>
            <Text style={tw`text-gray-500 text-center`}>
              {t('community_translation.no_contributors', currentLanguage)}
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );

  return (
    <PremiumBackgroundUniversal>
      <ScreenWrapper>
        <HeaderBar 
          title={t('community_translation.screen_title', currentLanguage)}
          onBack={() => navigation.goBack()}
        />

        {/* Tab Navigation */}
        <View style={tw`flex-row bg-white rounded-lg p-1 mb-4`}>
          <TouchableOpacity
            style={[
              tw`flex-1 p-3 rounded-md`,
              activeTab === 'contribute' && tw`bg-blue-600`
            ]}
            onPress={() => setActiveTab('contribute')}
          >
            <Text style={[
              tw`text-center font-medium`,
              activeTab === 'contribute' ? tw`text-white` : tw`text-gray-600`
            ]}>
              {t('community_translation.tab_contribute', currentLanguage)}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              tw`flex-1 p-3 rounded-md`,
              activeTab === 'review' && tw`bg-green-600`
            ]}
            onPress={() => setActiveTab('review')}
          >
            <Text style={[
              tw`text-center font-medium`,
              activeTab === 'review' ? tw`text-white` : tw`text-gray-600`
            ]}>
              {t('community_translation.tab_review', currentLanguage)}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              tw`flex-1 p-3 rounded-md`,
              activeTab === 'leaderboard' && tw`bg-yellow-600`
            ]}
            onPress={() => setActiveTab('leaderboard')}
          >
            <Text style={[
              tw`text-center font-medium`,
              activeTab === 'leaderboard' ? tw`text-white` : tw`text-gray-600`
            ]}>
              {t('community_translation.tab_leaderboard', currentLanguage)}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Tab Content */}
        {activeTab === 'contribute' && renderContributeTab()}
        {activeTab === 'review' && renderReviewTab()}
        {activeTab === 'leaderboard' && renderLeaderboardTab()}
      </ScreenWrapper>
    </PremiumBackgroundUniversal>
  );
};

// Contribution Card Component
interface ContributionCardProps {
  contribution: TranslationContribution;
  onVote: (contributionId: string, voteType: 'up' | 'down') => void;
  onSelect: () => void;
  language: string;
}

const ContributionCard: React.FC<ContributionCardProps> = ({ contribution, onVote, onSelect, language }) => (
  <TouchableOpacity
    style={tw`bg-white rounded-lg p-4 mb-3 shadow-sm`}
    onPress={onSelect}
  >
    <View style={tw`flex-row justify-between items-start mb-2`}>
      <Text style={tw`text-sm text-gray-500 uppercase`}>
        {contribution.fieldName}
      </Text>
      <View style={tw`flex-row items-center`}>
        <Text style={tw`text-xs text-gray-400 mr-2`}>
          {contribution.status}
        </Text>
      </View>
    </View>

    <View style={tw`mb-3`}>
      <Text style={tw`text-gray-600 text-sm mb-1`}>
        {t('community_translation.original', language)}:
      </Text>
      <Text style={tw`text-gray-800 mb-2`}>
        {contribution.originalText}
      </Text>
      
      <Text style={tw`text-blue-600 text-sm mb-1`}>
        {t('community_translation.suggested', language)}:
      </Text>
      <Text style={tw`text-blue-800 font-medium`}>
        {contribution.suggestedText}
      </Text>
    </View>

    <View style={tw`flex-row justify-between items-center`}>
      <View style={tw`flex-row items-center`}>
        <TouchableOpacity
          style={tw`flex-row items-center mr-4`}
          onPress={() => onVote(contribution.id, 'up')}
        >
          <Text style={tw`text-green-600 mr-1`}>👍</Text>
          <Text style={tw`text-green-600 font-medium`}>
            {contribution.votesUp}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={tw`flex-row items-center`}
          onPress={() => onVote(contribution.id, 'down')}
        >
          <Text style={tw`text-red-600 mr-1`}>👎</Text>
          <Text style={tw`text-red-600 font-medium`}>
            {contribution.votesDown}
          </Text>
        </TouchableOpacity>
      </View>

      <Text style={tw`text-xs text-gray-400`}>
        {new Date(contribution.createdAt).toLocaleDateString()}
      </Text>
    </View>
  </TouchableOpacity>
);

// Review Card Component
interface ReviewCardProps {
  translation: any;
  language: string;
  onReview: (translationId: string, approved: boolean) => void;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ translation, language, onReview }) => (
  <View style={tw`bg-white rounded-lg p-4 mb-3 shadow-sm`}>
    <Text style={tw`text-lg font-medium mb-2`}>
      {translation.question}
    </Text>
    
    <View style={tw`mb-3`}>
      <Text style={tw`text-gray-600 text-sm mb-1`}>
        {t('community_translation.options', language)}:
      </Text>
      {translation.options?.map((option: string, index: number) => (
        <Text key={index} style={tw`text-gray-800 ml-2`}>
          • {option}
        </Text>
      ))}
    </View>

    {translation.explanation && (
      <View style={tw`mb-3`}>
        <Text style={tw`text-gray-600 text-sm mb-1`}>
          {t('community_translation.explanation', language)}:
        </Text>
        <Text style={tw`text-gray-800`}>
          {translation.explanation}
        </Text>
      </View>
    )}

    <View style={tw`flex-row justify-end`}>
      <TouchableOpacity
        style={tw`bg-red-100 px-4 py-2 rounded-lg mr-2`}
        onPress={() => onReview(translation.id, false)}
      >
        <Text style={tw`text-red-600 font-medium`}>
          {t('community_translation.needs_work', language)}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={tw`bg-green-100 px-4 py-2 rounded-lg`}
        onPress={() => onReview(translation.id, true)}
      >
        <Text style={tw`text-green-600 font-medium`}>
          {t('community_translation.approve', language)}
        </Text>
      </TouchableOpacity>
    </View>
  </View>
);

// Leaderboard Card Component
interface LeaderboardCardProps {
  contributor: any;
  rank: number;
  language: string;
}

const LeaderboardCard: React.FC<LeaderboardCardProps> = ({ contributor, rank, language }) => (
  <View style={tw`bg-white rounded-lg p-4 mb-3 shadow-sm flex-row items-center`}>
    <View style={tw`w-10 h-10 rounded-full bg-yellow-100 items-center justify-center mr-3`}>
      <Text style={tw`text-yellow-600 font-bold`}>
        #{rank}
      </Text>
    </View>

    <View style={tw`flex-1`}>
      <Text style={tw`font-medium text-gray-800`}>
        {contributor.user_profiles?.display_name || 'Anonymous'}
      </Text>
      <Text style={tw`text-sm text-gray-500`}>
        {contributor.approved_contributions} {t('community_translation.approved', language)} • 
        {contributor.total_contributions} {t('community_translation.total', language)}
      </Text>
    </View>

    <View style={tw`items-center`}>
      <Text style={tw`text-lg font-bold text-blue-600`}>
        {contributor.total_votes_up}
      </Text>
      <Text style={tw`text-xs text-gray-500`}>
        {t('community_translation.votes', language)}
      </Text>
    </View>
  </View>
);
